/**
 * ---------------------------------------------------------------------------------
 *
 * 	This is container component for all things appearing on the
 *  OverfittingPage including instructions text, buttons, and the VideoComponent.
 * 
 * ----------------------------------------------------------------------------------
 */

import React, { useState } from 'react';
import PermanentDrawerLeft from '../../../components/PermanentDrawerLeft';
import VideoComponent from "../components/VideoComponent";
import { Button } from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
import { makeStyles } from '@material-ui/core/styles';
import car from "../../../assets/car.jpg"
import DialogWindow from './components/DialogWindow';

const useStyles = makeStyles((theme) => ({
  root: {
    '& > *': {
      margin: theme.spacing(1),
      width: '25ch',
    },
  },
  content: {
    paddingLeft: '200px',
    paddingTop: '90px',
    backgroundColor: 'white',
    display: 'flex',
    flexDirection: 'row-reverse',
    paddingBottom: '30px',
  },
  buttons: {
    marginLeft: '10px'
  },
  heading: {
    fontSize: '25px',
    display: "flex",
    alignItems: "center",
  },
  overfittingText: {
    marginLeft: '35px',
    marginRight: '25px',
    marginTop: '25px',
    textAlign: 'justify',
  },
  popup: {
    marginLeft: '400px',
    marginRight: '30px'
  },
}));

export default function OverfittingPage() {

  const [firstLabel, setFirstLabel] = useState<string>('');
  const [secondLabel, setSecondLabel] = useState<string>('');
  const [disabled, setDisabled] = useState(false);
  const [savedLabels, setSavedLabels] = useState(false);
  const [open, setOpen] = useState(false);
  const classes = useStyles();

  const saveLabels = () => {
    setDisabled(true);
    setSavedLabels(true);
    console.log("first label", firstLabel);
  };

  const resetLabels = () => {
    setFirstLabel("");
    setSecondLabel("");
    setDisabled(false);
    setSavedLabels(false);
    console.log("first label", firstLabel);
  };

  // for opening the DialogWindow
  const handleClickOpen = () => {
    setOpen(true);
  };

  // for closing the DialogWindow
  const handleClose = () => {
    setOpen(false);
  };

  return (
    <div>
      <PermanentDrawerLeft></PermanentDrawerLeft>
      <main className={classes.content}>
        <div className={classes.overfittingText}>
          <p>
            In this section, we will explore the significance of diversity in
            data that we use to teach or "train" machine learning models. More
            specifically, we will see how having a diverse dataset can give more
            accurate results and prevent overfitting. Refer to the polynomial
            regression section for more details on overfitting.
          </p>
          <div className={classes.heading}>
            Instructions
            <div className={classes.popup}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleClickOpen}
              >
                Click Me!
              </Button>
              <DialogWindow
                image={car}
                open={open}
                handleClose={handleClose}
              ></DialogWindow>
            </div>
          </div>
          <p>
            <strong>Prediction Confidence:</strong> A value between 0 to 1
            indicating how confident our machine learning model is about its
            returned prediction.
            <br /> <br />
            <strong>Loss: </strong> A number indicating how poor the model's
            prediction was on a single test example.
          </p>
          <p>
            Select two different objects (for example a pen and a notepad), save
            their labels below, and capture 3 pictures of each using the two
            buttons below the webcam. While taking pictures, position both
            objects somewhat in the middle of the screen. Make sure that your
            object is placed in front of a plain background and there are no
            additional items coming in the frame. Once you're done, click on the
            Train button. By "training", you are basically teaching your machine
            learning algorithm about which image represents which object (first
            or second?). Once you see a notification that the training is
            complete, take another picture of one of the two items you used by
            clicking on the Test button. While taking the picture, position your
            object at a different position than before. For example, the top
            left corner of your screen. Now, see the predictions below and
            observe the accuracy. Every time you click on the Test button, a new
            picture will be captured and tested via your webcam.
            <br />
            Now, Reset and repeat the process of clicking images, training, and
            testing. However, this time make sure you create a diverse data set
            by capturing pictures such that your objects are placed in different
            positions on the screen. For example, in the first picture, you can
            hold your object in the center of the center. Then, while capturing
            the second picture, you can hold your object in the top right corner
            and so on. Now when you try to capture new pictures to test your
            model and guess the object, you will observe a higher accuracy.
          </p>
          <form className={classes.root} noValidate autoComplete="off">
            <TextField
              id="filled-basic"
              value={firstLabel}
              label="First Label"
              variant="filled"
              onChange={(event) => {
                setFirstLabel(event.target.value);
              }}
              disabled={disabled}
            />
            <TextField
              id="filled-basic"
              value={secondLabel}
              label="Second Label"
              variant="filled"
              onChange={(event) => {
                setSecondLabel(event.target.value);
              }}
              disabled={disabled}
            />
          </form>
          <br />
          <Button
            variant="contained"
            color="primary"
            onClick={() => saveLabels()}
          >
            Save Labels
          </Button>
          <Button
            className={classes.buttons}
            variant="contained"
            color="primary"
            onClick={() => resetLabels()}
          >
            Reset Labels
          </Button>
          <div className="popup"></div>
        </div>
        <div className="video">
          {/* Default value for numberOfEpochs = 20 here. We change only in the UnderfittingPage  */}
          <VideoComponent
            firstLabel={savedLabels ? firstLabel : "First"}
            secondLabel={savedLabels ? secondLabel : "Second"}
            numberOfEpochs={20}
          />
        </div>
      </main>
    </div>
  );
}